import * as React from 'react';
import styled from 'styled-components';
import VisibilitySensor from 'react-visibility-sensor';

import { useProgressiveImage } from '../../../utils/hooks/useProgressiveImage';

const StyledImage = styled.img<{ maxHeight?: string }>`
  width: 100%;
  opacity: 0;

  ${({ maxHeight }) => (maxHeight ? `max-height: ${maxHeight};` : '')}

  &.loaded {
    opacity: 1;
  }
`;

const StyledImageWrapper = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors.bodyShade};
  overflow: hidden;
  color: white;
`;

const StyledImageContentWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const StyledImageContent = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  z-index: 1;
`;

const StyledImageOverlay = styled.div<{ opacity?: number }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ opacity }) => `rgba(0, 0, 0, ${opacity ? opacity : 0.6})`};
`;

interface IImage extends React.HTMLAttributes<HTMLImageElement> {
  url: string;
  height?: string;
  overlayOpacity?: number;
  alt: string | undefined;
}

export const Image: React.FC<IImage> = React.memo(
  ({ className, url, height, overlayOpacity, children, alt }) => {
    const [visible, setVisible] = React.useState(false);
    const { loaded } = useProgressiveImage({
      url: url,
      visible: visible
    });

    const visibilityChanged = (isVisible: boolean) => {
      if (!visible && isVisible === true) {
        setVisible(true);
      }
    };

    return (
      <VisibilitySensor
        active={!visible}
        onChange={visibilityChanged}
        partialVisibility={true}
        offset={{ bottom: 0 }}
      >
        <StyledImageWrapper className={className}>
          <StyledImage
            src={url}
            maxHeight={height}
            className={loaded ? 'loaded' : ''}
            alt={alt}
          />

          {children ? (
            <StyledImageContentWrapper>
              <StyledImageContent>{children}</StyledImageContent>
              <StyledImageOverlay opacity={overlayOpacity} />
            </StyledImageContentWrapper>
          ) : null}
        </StyledImageWrapper>
      </VisibilitySensor>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.url === nextProps.url) {
      return true;
    }
    return false;
  }
);
